import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { PageHeading, Segmented } from '@hp/ui';
import { GeneralTab } from '../features/leads/general-tab';
import { ReceivedTab } from '../features/leads/received-tab';
import { RulesTab } from '../features/leads/rules-tab';

/**
 * The lead router's tabs. A tab is one entry here (key → its label `leads:tabs.<key>`) plus its
 * component in `features/leads/`; the bar, the default tab (the first) and the rendering follow from
 * this list. The active tab lives in `?tab=<key>`, so a tab can be linked to; `?lead=<id>` opens the
 * detail of one received lead (and implies `tab=received` when the tab is missing); `?campaign=<adCampaignId>`
 * opens the rules tab's editor for that campaign's rule (a new rule when it has none).
 */
const TABS = [
  { key: 'rules', Component: RulesTab },
  { key: 'general', Component: GeneralTab },
  { key: 'received', Component: ReceivedTab },
] as const satisfies ReadonlyArray<{
  key: string;
  Component: ComponentType;
}>;
type TabKey = (typeof TABS)[number]['key'];

/** Lead routing: who gets which incoming lead, and with which Pipedrive values. */
export default function Leads() {
  const { t } = useTranslation(['leads']);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? (params.has('lead') ? 'received' : params.has('campaign') ? 'rules' : null);
  const current = TABS.find((x) => x.key === tab) ?? TABS[0];
  const Active = current.Component;

  return (
    <div className="bo-sections">
      <PageHeading title={t('page.title')} description={t('page.description')} />
      <Segmented
        ariaLabel={t('page.tabsAria')}
        value={current.key}
        options={TABS.map((x) => ({ value: x.key, label: t(`tabs.${x.key}`) }))}
        onChange={(key: TabKey) => setParams(key === TABS[0].key ? {} : { tab: key })}
      />
      <Active />
    </div>
  );
}
